import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useBlocker } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../lib/useSubscription';
import { validateLogoFile, processLogoImage } from '../lib/imageUtils';
import { isValidHex, buildTheme, DEFAULT_PRIMARY, DEFAULT_ACCENT, THEME_PRESETS } from '../lib/themeUtils';
import { useOnboarding } from '../contexts/OnboardingContext';
import GreekTimeInput from './GreekTimeInput';
import Sidebar from './Sidebar';
import LoadingSpinner from './LoadingSpinner';
import TourBubble from './onboarding/TourBubble';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Shared day mappings (used by save, load, and shift creation)
const DAY_NAME_TO_NUM = {
  monday: 1, tuesday: 2, wednesday: 3, thursday: 4,
  friday: 5, saturday: 6, sunday: 7
};

const DAY_NUM_TO_NAME = {
  1: 'monday', 2: 'tuesday', 3: 'wednesday', 4: 'thursday',
  5: 'friday', 6: 'saturday', 7: 'sunday'
};

const WEEK_DAYS_GREEK = {
  monday: 'Δευτέρα',
  tuesday: 'Τρίτη',
  wednesday: 'Τετάρτη',
  thursday: 'Πέμπτη',
  friday: 'Παρασκευή',
  saturday: 'Σάββατο',
  sunday: 'Κυριακή'
};

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, clinic, loading, isAuthenticated, refreshClinicData } = useAuth();
  const { step: onboardingStep, nextStep: onboardingNextStep, goToStep } = useOnboarding();
  const [clinicId, setClinicId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('schedule');

  // Section refs for navigation
  const sectionScheduleRef = useRef(null);
  const sectionBookingsRef = useRef(null);
  const sectionOnlineBookingRef = useRef(null);
  const sectionPublicPageRef = useRef(null);
  const sectionAccessRef = useRef(null);
  const sectionRemindersRef = useRef(null);

  // Appointment reminder template state
  const [appointmentEmailTemplate, setAppointmentEmailTemplate] = useState('');
  const [templateError, setTemplateError] = useState('');

  // Appointment confirmation template state
  const [appointmentConfirmationTemplate, setAppointmentConfirmationTemplate] = useState('');
  const [confirmationTemplateError, setConfirmationTemplateError] = useState('');

  // Online booking state
  const [clinicSlug, setClinicSlug] = useState('');
  const [slugSaving, setSlugSaving] = useState(false);
  const [slugCopied, setSlugCopied] = useState(false);

  const scrollToSection = (sectionKey, ref) => {
    setActiveSection(sectionKey);
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Day groups state (working schedule)
  const [dayGroups, setDayGroups] = useState([
    {
      id: 1,
      days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      hours: [{ id: 1, start: '09:00', end: '17:00' }],
      isClosed: false
    }
  ]);

  const [appointmentDuration, setAppointmentDuration] = useState(30);
  const [maxBookingMonths, setMaxBookingMonths] = useState(3);
  const [requiresConfirmation, setRequiresConfirmation] = useState(false);

  const [exceptionDays, setExceptionDays] = useState([
    { id: 1, date: '', reason: '', isClosed: true, isYearly: false }
  ]);

  const [specialDays, setSpecialDays] = useState([
    { id: 1, date: '', start: '09:00', end: '17:00', description: '', isYearly: false }
  ]);

  // Banned contacts state (managed independently, not via main save)
  const [bannedContacts, setBannedContacts] = useState([]);
  const [bannedForm, setBannedForm] = useState({ email: '', phone: '', reason: '' });
  const [bannedSaving, setBannedSaving] = useState(false);

  // Public page customization state
  const [logoUrl, setLogoUrl] = useState('');
  const [logoPreview, setLogoPreview] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY);
  const [accentColor, setAccentColor] = useState(DEFAULT_ACCENT);
  const [publicIntroText, setPublicIntroText] = useState('');
  const [themeId, setThemeId] = useState('light');

  // Stable ID counter (avoids duplicates from Math.max after removals)
  const idCounter = useRef(100);
  const nextId = () => ++idCounter.current;

  // Unsaved changes tracking
  const [isDirty, setIsDirty] = useState(false);
  const savedSnapshot = useRef(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const pendingPopBack = useRef(false);
  const bypassBlockerRef = useRef(false);

  // Block in-app React Router navigation when dirty
  const blocker = useBlocker(() => isDirty && !bypassBlockerRef.current);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      pendingPopBack.current = false;
      setShowLeaveModal(true);
    }
  }, [blocker]);

  // Onboarding tour
  useEffect(() => {
    if (!onboardingStep) return;

    if (onboardingStep === 'settings-intro') {
      goToStep('settings-schedule');
      return;
    }

    const sectionMap = {
      'settings-schedule': { key: 'schedule', ref: sectionScheduleRef },
      'settings-bookings': { key: 'bookings', ref: sectionBookingsRef },
      'settings-online': { key: 'online', ref: sectionOnlineBookingRef },
      'settings-publicpage': { key: 'publicpage', ref: sectionPublicPageRef },
      'settings-reminders': { key: 'reminders', ref: sectionRemindersRef },
      'settings-access': { key: 'access', ref: sectionAccessRef },
    };

    const target = sectionMap[onboardingStep];
    if (target) {
      const t = setTimeout(() => scrollToSection(target.key, target.ref), 150);
      return () => clearTimeout(t);
    }
  }, [onboardingStep]);

  // Toast notification state
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const toastTimeoutRef = useRef(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  // Resolve clinic ID
  useEffect(() => {
    if (clinic?.id) {
      setClinicId(clinic.id);
      if (clinic.slug) setClinicSlug(clinic.slug);
      return;
    }
    if (user && !loading) {
      supabase
        .from('vets')
        .select('clinic_id')
        .eq('user_id', user.id)
        .single()
        .then(({ data }) => {
          if (data?.clinic_id) setClinicId(data.clinic_id);
        });
    }
  }, [user, clinic, loading]);

  const { canCustomize, loading: subscriptionLoading } = useSubscription(clinicId);

  useEffect(() => {
    if (clinicId) {
      loadExistingSettings();
      loadBannedContacts();
    }
  }, [clinicId]);

  useEffect(() => {
    if (clinic) {
      if (clinic.logo_url) {
        setLogoUrl(clinic.logo_url);
        setLogoPreview(clinic.logo_url);
      }
      if (clinic.primary_color) setPrimaryColor(clinic.primary_color);
      if (clinic.accent_color) setAccentColor(clinic.accent_color);
      if (clinic.public_intro_text) setPublicIntroText(clinic.public_intro_text);
      if (clinic.theme_id) setThemeId(clinic.theme_id);
      
      if (savedSnapshot.current !== null) {
        savedSnapshot.current = {
          ...savedSnapshot.current,
          primaryColor: clinic.primary_color || DEFAULT_PRIMARY,
          accentColor: clinic.accent_color || DEFAULT_ACCENT,
          publicIntroText: clinic.public_intro_text || '',
          themeId: clinic.theme_id || 'light',
        };
      }
    }
  }, [clinic]);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  // Track form changes
  useEffect(() => {
    if (savedSnapshot.current === null) return;
    const current = JSON.stringify({ dayGroups, appointmentDuration, maxBookingMonths, requiresConfirmation, exceptionDays, specialDays, primaryColor, accentColor, publicIntroText, themeId, appointmentEmailTemplate, appointmentConfirmationTemplate });
    setIsDirty(current !== JSON.stringify(savedSnapshot.current));
  }, [dayGroups, appointmentDuration, maxBookingMonths, requiresConfirmation, exceptionDays, specialDays, primaryColor, accentColor, publicIntroText, themeId, appointmentEmailTemplate, appointmentConfirmationTemplate]);

  // Warn on browser tab close / refresh
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Block browser back/forward when dirty
  useEffect(() => {
    if (!isDirty) return;
    const handlePopState = () => {
      pendingPopBack.current = true;
      setShowLeaveModal(true);
    };
    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isDirty]);

  const showToast = (message, type = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, type });
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 4000);
  };

  // --- Day group management ---
  const addDayGroup = () => {
    setDayGroups([...dayGroups, {
      id: nextId(),
      days: [],
      hours: [{ id: 1, start: '09:00', end: '17:00' }],
      isClosed: false
    }]);
  };

  const removeDayGroup = (groupId) => {
    if (dayGroups.length > 1) {
      setDayGroups(dayGroups.filter(g => g.id !== groupId));
    }
  };

  const updateDayGroupDays = (groupId, day, checked) => {
    setDayGroups(dayGroups.map(g => {
      if (g.id === groupId) {
        if (checked) {
          return { ...g, days: [...g.days, day] };
        } else {
          return { ...g, days: g.days.filter(d => d !== day) };
        }
      }
      return g;
    }));
  };

  const updateDayGroupHours = (groupId, hourId, field, value) => {
    setDayGroups(dayGroups.map(g => {
      if (g.id === groupId) {
        return {
          ...g,
          hours: g.hours.map(h => h.id === hourId ? { ...h, [field]: value } : h)
        };
      }
      return g;
    }));
  };

  const addHourToGroup = (groupId) => {
    setDayGroups(dayGroups.map(g => {
      if (g.id === groupId) {
        return {
          ...g,
          hours: [...g.hours, { id: nextId(), start: '09:00', end: '17:00' }]
        };
      }
      return g;
    }));
  };

  const removeHourFromGroup = (groupId, hourId) => {
    setDayGroups(dayGroups.map(g => {
      if (g.id === groupId) {
        if (g.hours.length > 1) {
          return { ...g, hours: g.hours.filter(h => h.id !== hourId) };
        }
      }
      return g;
    }));
  };

  const toggleDayGroupClosed = (groupId) => {
    setDayGroups(dayGroups.map(g => {
      if (g.id === groupId) {
        return { ...g, isClosed: !g.isClosed };
      }
      return g;
    }));
  };

  // --- Exception days management ---
  const addExceptionDay = () => {
    setExceptionDays([...exceptionDays, { id: nextId(), date: '', reason: '', isClosed: true, isYearly: false }]);
  };

  const removeExceptionDay = (id) => {
    if (exceptionDays.length > 1) {
      setExceptionDays(exceptionDays.filter(d => d.id !== id));
    }
  };

  const updateExceptionDay = (id, field, value) => {
    setExceptionDays(exceptionDays.map(d =>
      d.id === id ? { ...d, [field]: value } : d
    ));
  };

  // --- Special days management ---
  const addSpecialDay = () => {
    setSpecialDays([...specialDays, { id: nextId(), date: '', start: '09:00', end: '17:00', description: '', isYearly: false }]);
  };

  const removeSpecialDay = (id) => {
    setSpecialDays(specialDays.filter(d => d.id !== id));
  };

  const updateSpecialDay = (id, field, value) => {
    setSpecialDays(specialDays.map(d =>
      d.id === id ? { ...d, [field]: value } : d
    ));
  };

  // --- Banned contacts management ---
  const loadBannedContacts = async () => {
    const { data, error } = await supabase
      .from('settings_banned_contacts')
      .select('*')
      .eq('clinic_id', clinicId)
      .order('created_at', { ascending: false });
    if (!error && data) {
      setBannedContacts(data);
    }
  };

  const addBannedContact = async () => {
    if (!bannedForm.email && !bannedForm.phone) {
      showToast('Συμπληρώστε τουλάχιστον email ή τηλέφωνο.', 'error');
      return;
    }

    setBannedSaving(true);
    try {
      const { data, error } = await supabase
        .from('settings_banned_contacts')
        .insert({
          clinic_id: clinicId,
          email: bannedForm.email || null,
          phone: bannedForm.phone || null,
          reason: bannedForm.reason || null
        })
        .select()
        .single();

      if (error) throw error;

      setBannedContacts([data, ...bannedContacts]);
      setBannedForm({ email: '', phone: '', reason: '' });
      showToast('Η επαφή αποκλείστηκε επιτυχώς.', 'success');
    } catch (err) {
      showToast(`Σφάλμα: ${err.message}`, 'error');
    } finally {
      setBannedSaving(false);
    }
  };

  const removeBannedContact = async (id) => {
    try {
      const { error } = await supabase
        .from('settings_banned_contacts')
        .delete()
        .eq('id', id);
      if (error) throw error;

      setBannedContacts(bannedContacts.filter(c => c.id !== id));
      showToast('Η επαφή αφαιρέθηκε από τη λίστα.', 'success');
    } catch (err) {
      showToast(`Σφάλμα διαγραφής: ${err.message}`, 'error');
    }
  };

  // --- Template helpers ---
  const generateDefaultTemplate = () => {
    const clinicName = clinic?.name || 'Η κλινική μας';
    const clinicPhone = clinic?.line_phone || clinic?.mobile_phone || '';
    return `Αγαπητέ/ή {{owner_name}},

Σας υπενθυμίζουμε ότι έχετε προγραμματισμένο ραντεβού για το κατοικίδιό σας {{pet_name}} στις {{appointment_date}}.
Παρακαλούμε επιβεβαιώστε την παρουσία σας ή επικοινωνήστε μαζί μας για τυχόν αλλαγές.

Με εκτίμηση,
${clinicName}${clinicPhone ? `\nΤηλ: ${clinicPhone}` : ''}`;
  };

  const generateDefaultConfirmationTemplate = () => {
    const clinicName = clinic?.name || 'Η κλινική μας';
    const clinicPhone = clinic?.line_phone || clinic?.mobile_phone || '';

    return `Αγαπητέ/ή {{owner_name}},

Το ραντεβού σας για το κατοικίδιό σας {{pet_name}} στις {{appointment_date}} έχει επιβεβαιωθεί.
Σας περιμένουμε! Εάν χρειαστεί να αλλάξετε ή να ακυρώσετε το ραντεβού, παρακαλούμε επικοινωνήστε μαζί μας εγκαίρως.

Με εκτίμηση,
${clinicName}${clinicPhone ? `\nΤηλ: ${clinicPhone}` : ''}`;
  };

  const validateTemplate = (template) => {
    if (!template) return null;
    const requiredTags = ['{{pet_name}}', '{{appointment_date}}', '{{owner_name}}'];
    const missing = requiredTags.filter(tag => !template.includes(tag));
    if (missing.length > 0) {
      return `Λείπουν τα υποχρεωτικά πεδία: ${missing.join(', ')}`;
    }
    return null;
  };

  // --- Validation ---
  const validateSettings = () => {
    if (dayGroups.length === 0) return 'Πρέπει να υπάρχει τουλάχιστον μία ομάδα ημερών.';
    const hasWorkingDays = dayGroups.some(group => !group.isClosed && group.days.length > 0);
    if (!hasWorkingDays) return 'Πρέπει να υπάρχει τουλάχιστον μία ημέρα εργασίας.';

    for (const group of dayGroups) {
      if (!group.isClosed && group.days.length > 0) {
        if (group.hours.length === 0) return `Η ομάδα ${group.id} δεν έχει ώρες εργασίας.`;
        for (const hour of group.hours) {
          if (!hour.start || !hour.end) return `Η ομάδα ${group.id} έχει ελλιπείς ώρες.`;
          if (hour.start >= hour.end) return `Η ομάδα ${group.id} έχει λάθος ώρες (η αρχή πρέπει να είναι πριν το τέλος).`;
        }
      }
    }

    for (const day of exceptionDays) {
      if (day.date && day.date.trim() !== '' && !day.isYearly) {
        if (new Date(day.date) < new Date()) return 'Οι ημέρες εξαίρεσης (μη ετήσιες) δεν μπορούν να είναι στο παρελθόν.';
      }
    }

    for (const day of specialDays) {
      if (day.date && day.date.trim() !== '') {
        if (!day.isYearly && new Date(day.date) < new Date()) return 'Οι ειδικές ημέρες (μη ετήσιες) δεν μπορούν να είναι στο παρελθόν.';
        if (!day.start || !day.end || day.start >= day.end) return 'Οι ειδικές ημέρες πρέπει να έχουν έγκυρες ώρες.';
      }
    }

    return null;
  };

  // --- Save functions ---
  const saveSettings = async () => {
    setSaving(true);
    try {
      if (!clinicId) throw new Error('Δεν βρέθηκε κλινική για αυτόν τον χρήστη.');
      const validationError = validateSettings();
      if (validationError) throw new Error(validationError);

      const savePromises = [
        saveClinicSettings(),
        saveWorkingSchedules(),
        saveExceptionDays(),
        saveSpecialDays()
      ];
      if (canCustomize) savePromises.push(savePublicPageSettings());
      
      const results = await Promise.allSettled(savePromises);
      const failures = results.filter(result => result.status === 'rejected');
      if (failures.length > 0) {
        const errorMessages = failures.map(f => f.reason?.message || 'Unknown error').join(', ');
        throw new Error(`Σφάλμα κατά την αποθήκευση: ${errorMessages}`);
      }

      showToast('Όλες οι ρυθμίσεις αποθηκεύτηκαν επιτυχώς!', 'success');
      savedSnapshot.current = { dayGroups, appointmentDuration, maxBookingMonths, requiresConfirmation, exceptionDays, specialDays, primaryColor, accentColor, publicIntroText, themeId, appointmentEmailTemplate, appointmentConfirmationTemplate };
      setIsDirty(false);
      await refreshClinicData();
      await createMainShiftFromSettings();
    } catch (error) {
      showToast(`Σφάλμα κατά την αποθήκευση: ${error.message}`, 'error');
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const saveClinicSettings = async () => {
    if (appointmentEmailTemplate) {
      const templateValidationError = validateTemplate(appointmentEmailTemplate);
      if (templateValidationError) throw new Error(templateValidationError);
    }

    if (appointmentConfirmationTemplate) {
      const confirmationValidationError = validateTemplate(appointmentConfirmationTemplate);
      if (confirmationValidationError) throw new Error(confirmationValidationError);
    }

    const { error } = await supabase
      .from('settings_clinic_settings')
      .upsert({
        clinic_id: clinicId,
        appointment_duration_minutes: appointmentDuration,
        max_booking_months: maxBookingMonths,
        requires_confirmation: requiresConfirmation,
        appointment_email_template: appointmentEmailTemplate || null,
        appointment_confirmation_template: appointmentConfirmationTemplate || null
      }, {
        onConflict: 'clinic_id'
      });

    if (error) throw new Error(`Σφάλμα αποθήκευσης ρυθμίσεων κλινικής: ${error.message}`);
  };

  const saveWorkingSchedules = async () => {
    const { error: deleteError } = await supabase.from('settings_clinic_working_schedules').delete().eq('clinic_id', clinicId);
    if (deleteError) throw new Error(`Σφάλμα διαγραφής υπάρχοντων προγραμμάτων: ${deleteError.message}`);

    for (let i = 0; i < dayGroups.length; i++) {
      const group = dayGroups[i];
      const { data: scheduleData, error: scheduleError } = await supabase
        .from('settings_clinic_working_schedules')
        .insert({ clinic_id: clinicId, group_name: `Ομάδα ${i + 1}`, is_closed: group.isClosed, sort_order: i })
        .select().single();
      if (scheduleError) throw new Error(`Σφάλμα δημιουργίας ομάδας ημερών: ${scheduleError.message}`);

      if (group.days.length > 0) {
        const dayInserts = group.days.map(day => ({ schedule_id: scheduleData.id, weekday_id: DAY_NAME_TO_NUM[day] }));
        const { error: daysError } = await supabase.from('settings_clinic_schedule_days').insert(dayInserts);
        if (daysError) throw new Error(`Σφάλμα αποθήκευσης ημερών: ${daysError.message}`);
      }

      if (group.hours.length > 0 && !group.isClosed) {
        const hourInserts = group.hours.map((hour, index) => ({
          schedule_id: scheduleData.id, start_time: hour.start, end_time: hour.end, sort_order: index
        }));
        const { error: hoursError } = await supabase.from('settings_clinic_schedule_hours').insert(hourInserts);
        if (hoursError) throw new Error(`Σφάλμα αποθήκευσης ωρών: ${hoursError.message}`);
      }
    }
  };

  const saveExceptionDays = async () => {
    const { error: deleteError } = await supabase.from('settings_clinic_exception_days').delete().eq('clinic_id', clinicId);
    if (deleteError) throw new Error(`Σφάλμα διαγραφής υπάρχοντων ημερών εξαίρεσης: ${deleteError.message}`);

    const validExceptionDays = exceptionDays.filter(day => day.date.trim() !== '');
    if (validExceptionDays.length > 0) {
      const exceptionInserts = validExceptionDays.map(day => ({
        clinic_id: clinicId, exception_date: day.date, reason: day.reason || null, is_closed: day.isClosed, is_yearly: day.isYearly || false
      }));
      const { error: insertError } = await supabase.from('settings_clinic_exception_days').insert(exceptionInserts);
      if (insertError) throw new Error(`Σφάλμα αποθήκευσης ημερών εξαίρεσης: ${insertError.message}`);
    }
  };

  const saveSpecialDays = async () => {
    const { error: deleteError } = await supabase.from('settings_clinic_special_days').delete().eq('clinic_id', clinicId);
    if (deleteError) throw new Error(`Σφάλμα διαγραφής υπάρχοντων ειδικών ημερών: ${deleteError.message}`);

    const validSpecialDays = specialDays.filter(day => day.date.trim() !== '');
    if (validSpecialDays.length > 0) {
      const specialInserts = validSpecialDays.map(day => ({
        clinic_id: clinicId, special_date: day.date, description: day.description || null, start_time: day.start, end_time: day.end, is_yearly: day.isYearly || false
      }));
      const { error: insertError } = await supabase.from('settings_clinic_special_days').insert(specialInserts);
      if (insertError) throw new Error(`Σφάλμα αποθήκευσης ειδικών ημερών: ${insertError.message}`);
    }
  };

  const createMainShiftFromSettings = async () => {
    try {
      const { data: existingShifts, error: checkError } = await supabase.from('shifts').select('id').eq('clinic_id', clinicId).eq('name', 'Κύρια Βάρδια');
      if (checkError) return;

      if (existingShifts && existingShifts.length > 0) {
        const shiftIds = existingShifts.map(s => s.id);
        await supabase.from('shifts').delete().in('id', shiftIds);
      }

      const workingSchedules = dayGroups
        .filter(group => !group.isClosed)
        .map((group, index) => ({
          name: `Ομάδα ${index + 1}`,
          days: group.days.map(day => DAY_NAME_TO_NUM[day]).filter(Boolean),
          hours: group.hours.map(hour => ({ start_time: hour.start, end_time: hour.end }))
        }))
        .filter(schedule => schedule.days.length > 0 && schedule.hours.length > 0);

      if (workingSchedules.length === 0) return;

      const { data: shiftData, error: shiftError } = await supabase.from('shifts').insert({
        clinic_id: clinicId, name: 'Κύρια Βάρδια', from_date: new Date().toISOString().split('T')[0], to_date: null, is_infinite: true, is_active: true
      }).select().single();

      if (shiftError) return;

      for (let i = 0; i < workingSchedules.length; i++) {
        const schedule = workingSchedules[i];
        const { data: dayGroupData, error: dayGroupError } = await supabase.from('shift_day_groups').insert({
          shift_id: shiftData.id, name: schedule.name, sort_order: i
        }).select().single();

        if (dayGroupError) continue;

        const dayInserts = schedule.days.map(dayId => ({ day_group_id: dayGroupData.id, weekday_id: dayId }));
        if (dayInserts.length > 0) await supabase.from('shift_days').insert(dayInserts);

        const hourInserts = schedule.hours.map((hour, j) => ({
          day_group_id: dayGroupData.id, start_time: hour.start_time, end_time: hour.end_time, sort_order: j
        }));
        if (hourInserts.length > 0) await supabase.from('shift_hours').insert(hourInserts);
      }
    } catch (error) {
      console.error('Error creating main shift from settings:', error);
    }
  };

  // --- Public page: logo upload/remove ---
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateLogoFile(file);
    if (!validation.valid) return showToast(validation.error, 'error');

    setLogoUploading(true);
    try {
      const blob = await processLogoImage(file);
      const filePath = `${clinicId}.webp`;
      const { error: uploadError } = await supabase.storage.from('clinic-logos').upload(filePath, blob, { contentType: 'image/webp', upsert: true });
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from('clinic-logos').getPublicUrl(filePath);
      const versionedUrl = `${publicUrl}?v=${Date.now()}`;
      const { error: updateError } = await supabase.from('clinics').update({ logo_url: versionedUrl }).eq('id', clinicId);
      if (updateError) throw updateError;

      setLogoUrl(versionedUrl);
      setLogoPreview(versionedUrl);
      await refreshClinicData();
      showToast('Το λογότυπο ανέβηκε επιτυχώς!', 'success');
    } catch (err) {
      showToast(`Σφάλμα ανεβάσματος: ${err.message}`, 'error');
    } finally {
      setLogoUploading(false);
      e.target.value = '';
    }
  };

  const handleLogoRemove = async () => {
    setLogoUploading(true);
    try {
      const { error: deleteError } = await supabase.storage.from('clinic-logos').remove([`${clinicId}.webp`]);
      if (deleteError) throw deleteError;

      const { error: updateError } = await supabase.from('clinics').update({ logo_url: null }).eq('id', clinicId);
      if (updateError) throw updateError;

      setLogoUrl('');
      setLogoPreview('');
      await refreshClinicData();
      showToast('Το λογότυπο αφαιρέθηκε.', 'success');
    } catch (err) {
      showToast(`Σφάλμα διαγραφής: ${err.message}`, 'error');
    } finally {
      setLogoUploading(false);
    }
  };

  const savePublicPageSettings = async () => {
    if (!canCustomize) return;
    const updates = {};
    if (primaryColor && !isValidHex(primaryColor)) throw new Error('Μη έγκυρο χρώμα (primary). Χρησιμοποιήστε μορφή #RRGGBB.');
    if (accentColor && !isValidHex(accentColor)) throw new Error('Μη έγκυρο χρώμα (accent). Χρησιμοποιήστε μορφή #RRGGBB.');
    if (publicIntroText && publicIntroText.length > 200) throw new Error('Το κείμενο εισαγωγής δεν πρέπει να υπερβαίνει τους 200 χαρακτήρες.');

    updates.primary_color = primaryColor || null;
    updates.accent_color = accentColor || null;
    updates.public_intro_text = publicIntroText.replace(/<[^>]*>/g, '').trim() || null;
    updates.theme_id = themeId || 'light';

    const { error } = await supabase.from('clinics').update(updates).eq('id', clinicId);
    if (error) throw new Error(`Σφάλμα αποθήκευσης δημόσιας σελίδας: ${error.message}`);
  };

  // --- Online Booking slug management ---
  const generateSlugFromName = async () => {
    if (!clinic?.name) return;
    let slug = clinic.name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    if (!slug) slug = 'clinic-' + Math.random().toString(36).substring(2, 10);
    setClinicSlug(slug);

    setSlugSaving(true);
    try {
      const { error } = await supabase.from('clinics').update({ slug: slug.trim() }).eq('id', clinicId);
      if (error) {
        if (error.code === '23505') showToast('Αυτό το URL χρησιμοποιείται ήδη. Δοκιμάστε ένα διαφορετικό.', 'error');
        else showToast('Σφάλμα κατά την αποθήκευση', 'error');
        return;
      }
      showToast('Το URL δημιουργήθηκε και αποθηκεύτηκε!', 'success');
      await refreshClinicData();
    } catch (err) {
      showToast('Σφάλμα κατά την αποθήκευση', 'error');
    } finally {
      setSlugSaving(false);
    }
  };

  const saveSlug = async () => {
    if (!clinicSlug.trim()) return showToast('Παρακαλώ εισάγετε ένα URL slug', 'error');
    if (!/^[a-z0-9-]+$/.test(clinicSlug)) return showToast('Το slug μπορεί να περιέχει μόνο πεζά γράμματα, αριθμούς και παύλες', 'error');
    
    setSlugSaving(true);
    try {
      const { error } = await supabase.from('clinics').update({ slug: clinicSlug.trim() }).eq('id', clinicId);
      if (error) {
        if (error.code === '23505') showToast('Αυτό το URL χρησιμοποιείται ήδη. Δοκιμάστε ένα διαφορετικό.', 'error');
        else showToast('Σφάλμα κατά την αποθήκευση', 'error');
        return;
      }
      showToast('Το URL αποθηκεύτηκε επιτυχώς!', 'success');
      await refreshClinicData();
    } catch (err) {
      showToast('Σφάλμα κατά την αποθήκευση', 'error');
    } finally {
      setSlugSaving(false);
    }
  };

  const copyBookingUrl = () => {
    const url = `${window.location.origin}/book/${clinicSlug}`;
    navigator.clipboard.writeText(url).then(() => {
      setSlugCopied(true);
      setTimeout(() => setSlugCopied(false), 2000);
    });
  };

  const downloadQR = () => {
    const svg = document.getElementById('booking-qr-code');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const qrImg = new Image();
    qrImg.onload = () => {
      canvas.width = 400;
      canvas.height = 400;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 400, 400);
      ctx.drawImage(qrImg, 0, 0, 400, 400);
      const logo = new Image();
      logo.onload = () => {
        const logoSize = 100;
        const x = (400 - logoSize) / 2;
        const y = (400 - logoSize) / 2;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(x - 4, y - 4, logoSize + 8, logoSize + 8);
        ctx.drawImage(logo, x, y, logoSize, logoSize);
        const pngUrl = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = `${clinicSlug}-qr-code.png`;
        downloadLink.click();
      };
      logo.onerror = () => {
        const pngUrl = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = `${clinicSlug}-qr-code.png`;
        downloadLink.click();
      };
      logo.src = '/favicon.png';
    };
    qrImg.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const loadExistingSettings = async () => {
    try {
      const [clinicSettingsResult, schedulesResult, exceptionResult, specialResult] = await Promise.all([
        supabase.from('settings_clinic_settings').select('*').eq('clinic_id', clinicId).single(),
        supabase.from('settings_clinic_working_schedules').select(`*, settings_clinic_schedule_days(weekday_id), settings_clinic_schedule_hours(*)`).eq('clinic_id', clinicId).order('sort_order'),
        supabase.from('settings_clinic_exception_days').select('*').eq('clinic_id', clinicId).order('exception_date'),
        supabase.from('settings_clinic_special_days').select('*').eq('clinic_id', clinicId).order('special_date')
      ]);

      if (clinicSettingsResult.data && !clinicSettingsResult.error) {
        setAppointmentDuration(clinicSettingsResult.data.appointment_duration_minutes || 30);
        setMaxBookingMonths(clinicSettingsResult.data.max_booking_months || 3);
        setRequiresConfirmation(clinicSettingsResult.data.requires_confirmation || false);
        if (clinicSettingsResult.data.appointment_email_template) setAppointmentEmailTemplate(clinicSettingsResult.data.appointment_email_template);
        if (clinicSettingsResult.data.appointment_confirmation_template) setAppointmentConfirmationTemplate(clinicSettingsResult.data.appointment_confirmation_template);
      }

      let snapshotDayGroups;
      if (schedulesResult.data && !schedulesResult.error && schedulesResult.data.length > 0) {
        const loadedDayGroups = schedulesResult.data.map((schedule, index) => {
          const days = schedule.settings_clinic_schedule_days?.map(d => DAY_NUM_TO_NAME[d.weekday_id]) || [];
          const hours = schedule.settings_clinic_schedule_hours?.map(h => ({ id: h.id, start: h.start_time, end: h.end_time })) || [];
          return { id: index + 1, days: days, hours: hours.length > 0 ? hours : [{ id: 1, start: '09:00', end: '17:00' }], isClosed: schedule.is_closed };
        });
        setDayGroups(loadedDayGroups);
        snapshotDayGroups = loadedDayGroups;
      } else {
        createDefaultWorkingSchedule();
        snapshotDayGroups = [{ id: 1, days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'], hours: [{ id: 1, start: '09:00', end: '17:00' }], isClosed: false }];
      }

      let snapshotExceptionDays = [{ id: 1, date: '', reason: '', isClosed: true, isYearly: false }];
      if (exceptionResult.data && !exceptionResult.error && exceptionResult.data.length > 0) {
        const loadedExceptionDays = exceptionResult.data.map((day, index) => ({
          id: index + 1, date: day.exception_date, reason: day.reason || '', isClosed: day.is_closed, isYearly: day.is_yearly || false
        }));
        setExceptionDays(loadedExceptionDays);
        snapshotExceptionDays = loadedExceptionDays;
      }

      let snapshotSpecialDays = [{ id: 1, date: '', start: '09:00', end: '17:00', description: '', isYearly: false }];
      if (specialResult.data && !specialResult.error && specialResult.data.length > 0) {
        const loadedSpecialDays = specialResult.data.map((day, index) => ({
          id: index + 1, date: day.special_date, start: day.start_time, end: day.end_time, description: day.description || '', isYearly: day.is_yearly || false
        }));
        setSpecialDays(loadedSpecialDays);
        snapshotSpecialDays = loadedSpecialDays;
      }

      savedSnapshot.current = {
        dayGroups: snapshotDayGroups,
        appointmentDuration: clinicSettingsResult.data?.appointment_duration_minutes || 30,
        maxBookingMonths: clinicSettingsResult.data?.max_booking_months || 3,
        requiresConfirmation: clinicSettingsResult.data?.requires_confirmation || false,
        exceptionDays: snapshotExceptionDays,
        specialDays: snapshotSpecialDays,
        primaryColor: clinic?.primary_color || DEFAULT_PRIMARY,
        accentColor: clinic?.accent_color || DEFAULT_ACCENT,
        publicIntroText: clinic?.public_intro_text || '',
        themeId: clinic?.theme_id || 'light',
        appointmentEmailTemplate: clinicSettingsResult.data?.appointment_email_template || '',
        appointmentConfirmationTemplate: clinicSettingsResult.data?.appointment_confirmation_template || '',
      };
    } catch (error) {
      console.error('Error loading existing settings:', error);
      createDefaultWorkingSchedule();
      savedSnapshot.current = {
        dayGroups: [{ id: 1, days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'], hours: [{ id: 1, start: '09:00', end: '17:00' }], isClosed: false }],
        appointmentDuration: 30,
        maxBookingMonths: 3,
        requiresConfirmation: false,
        exceptionDays: [{ id: 1, date: '', reason: '', isClosed: true, isYearly: false }],
        specialDays: [{ id: 1, date: '', start: '09:00', end: '17:00', description: '', isYearly: false }],
        primaryColor: clinic?.primary_color || DEFAULT_PRIMARY,
        accentColor: clinic?.accent_color || DEFAULT_ACCENT,
        publicIntroText: clinic?.public_intro_text || '',
        themeId: clinic?.theme_id || 'light',
        appointmentEmailTemplate: '',
        appointmentConfirmationTemplate: '',
      };
    }
  };

  const createDefaultWorkingSchedule = () => {
    setDayGroups([{ id: 1, days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'], hours: [{ id: 1, start: '09:00', end: '17:00' }], isClosed: false }]);
    setExceptionDays([{ id: 1, date: '', reason: '', isClosed: true, isYearly: false }]);
    setSpecialDays([{ id: 1, date: '', start: '09:00', end: '17:00', description: '', isYearly: false }]);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen text-white flex flex-col lg:flex-row" style={{ backgroundColor: '#1A202C', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, background: 'radial-gradient(800px 800px at 10% -10%, rgba(16,185,129,0.12), transparent), radial-gradient(800px 800px at 110% 10%, rgba(59,130,246,0.08), transparent)' }} className="pointer-events-none" />
      <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />

      {/* Main Content */}
      <div className="flex-1 p-4 lg:p-8 flex flex-col relative h-screen overflow-hidden">
        
        <div className="flex-1 overflow-y-auto custom-scrollbar pb-32">
          {/* Header */}
          <div className="mb-6 lg:mb-8">
            <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">Ρυθμίσεις Κλινικής</h1>
            <p className="text-gray-400 text-base lg:text-lg">
              Διαχειριστείτε τις ρυθμίσεις λειτουργίας της κλινικής σας
            </p>
          </div>

          {!clinicId ? (
            <div className="rounded-2xl border p-8 text-center bg-[#111827] border-gray-700 shadow-xl mb-6">
              <svg className="mx-auto h-16 w-16 text-gray-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <h3 className="text-xl font-bold text-white mb-2">Δεν βρέθηκε κλινική</h3>
              <p className="text-gray-400">
                Για να χρησιμοποιήσετε τη λειτουργικότητα των ρυθμίσεων, πρέπει πρώτα να δημιουργήσετε την κλινική σας.
              </p>
            </div>
          ) : (
            <>
              {/* Section Navigation (Pill Tabs) */}
              <div className="flex space-x-2 mb-8 p-1.5 rounded-xl overflow-x-auto bg-[#1F2937] border border-gray-700 custom-scrollbar sticky top-0 z-20 shadow-md">
                {[
                  { key: 'schedule', label: 'Ωράριο', ref: sectionScheduleRef, icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
                  { key: 'bookings', label: 'Κρατήσεις', ref: sectionBookingsRef, icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
                  { key: 'online', label: 'Online Κράτηση', ref: sectionOnlineBookingRef, icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1' },
                  { key: 'publicpage', label: 'Δημόσια Σελίδα', ref: sectionPublicPageRef, icon: 'M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01', badge: 'Pro' },
                  { key: 'reminders', label: 'Υπενθυμίσεις', ref: sectionRemindersRef, icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' },
                  { key: 'access', label: 'Πρόσβαση', ref: sectionAccessRef, icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
                ].map((section) => (
                  <button
                    key={section.key}
                    onClick={() => scrollToSection(section.key, section.ref)}
                    className={`flex items-center justify-center space-x-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all whitespace-nowrap flex-1 ${
                      activeSection === section.key
                        ? 'bg-emerald-600 text-white shadow-md'
                        : 'text-gray-400 hover:text-white hover:bg-gray-700'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={section.icon} />
                    </svg>
                    <span>{section.label}</span>
                    {section.badge && (
                      <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-white/20 text-white border border-white/30 ml-1">{section.badge}</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Settings Form Wrapper */}
              <div className="space-y-10">
                
                {/* ═══ GROUP A: Ωράριο Κλινικής ═══ */}
                <div ref={sectionScheduleRef} className="scroll-mt-24">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                      <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Ωράριο Κλινικής</h2>
                      <p className="text-sm text-gray-400">Εργάσιμες ημέρες, ώρες λειτουργίας και εξαιρέσεις</p>
                    </div>
                  </div>

                  {onboardingStep === 'settings-schedule' && (
                    <div className="mb-4">
                      <TourBubble icon="🕐" stepLabel="Ρυθμίσεις (1/6)" title="Ωράριο Κλινικής" message="Ορίστε τις εργάσιμες ημέρες και ώρες λειτουργίας. Μπορείτε να δημιουργήσετε διαφορετικά ωράρια για διαφορετικές ημέρες." arrowSide="top" actions={[{ label: 'Επόμενο →', onClick: onboardingNextStep, variant: 'primary' }]} />
                    </div>
                  )}

                  {/* Working Days & Hours */}
                  <div className="rounded-2xl border bg-[#111827] border-gray-700 shadow-lg overflow-hidden mb-6">
                    <div className="px-6 py-5 border-b border-gray-700 bg-[#111827]">
                      <h3 className="text-lg font-semibold text-white">Εργάσιμες Ημέρες & Ώρες</h3>
                      <p className="text-sm text-gray-400 mt-1">Οργανώστε τις ημέρες σε ομάδες και ορίστε τις ώρες λειτουργίας</p>
                    </div>
                    <div className="p-6 space-y-6">
                      {dayGroups.map((group) => (
                        <div key={group.id} className="p-5 rounded-xl border border-gray-700 bg-[#1F2937]">
                          <div className="flex items-center justify-between mb-5">
                            <h4 className="text-base font-bold text-white flex items-center gap-2">
                              Ομάδα {group.id} 
                              {group.isClosed && <span className="text-xs font-bold px-2 py-0.5 bg-red-500/20 text-red-400 border border-red-500/30 rounded-md">Κλειστό</span>}
                            </h4>
                            <div className="flex items-center gap-3">
                              {/* Custom Toggle Switch */}
                              <button 
                                onClick={() => toggleDayGroupClosed(group.id)} 
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none ${!group.isClosed ? 'bg-emerald-500' : 'bg-gray-600'}`}
                              >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${!group.isClosed ? 'translate-x-6' : 'translate-x-1'}`} />
                              </button>
                              
                              {dayGroups.length > 1 && (
                                <button onClick={() => removeDayGroup(group.id)} className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors" title="Διαγραφή">
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </button>
                              )}
                            </div>
                          </div>

                          {!group.isClosed && (
                            <>
                              <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-400 mb-3">Επιλέξτε ημέρες:</label>
                                <div className="flex flex-wrap gap-2">
                                  {Object.entries(WEEK_DAYS_GREEK).map(([key, label]) => {
                                    const isSelected = group.days.includes(key);
                                    return (
                                      <button
                                        key={key}
                                        onClick={() => updateDayGroupDays(group.id, key, !isSelected)}
                                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                                          isSelected 
                                            ? 'bg-blue-600 text-white border-blue-500 shadow-md' 
                                            : 'bg-[#111827] text-gray-400 border-gray-700 hover:border-gray-500 hover:text-gray-200'
                                        }`}
                                      >
                                        {label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-400 mb-3">Ώρες λειτουργίας:</label>
                                <div className="space-y-3">
                                  {group.hours.map((hours) => (
                                    <div key={hours.id} className="flex items-center gap-3 w-fit bg-[#111827] p-2 rounded-lg border border-gray-700">
                                      <GreekTimeInput value={hours.start} onChange={(value) => updateDayGroupHours(group.id, hours.id, 'start', value)} />
                                      <span className="text-gray-500 font-medium">έως</span>
                                      <GreekTimeInput value={hours.end} onChange={(value) => updateDayGroupHours(group.id, hours.id, 'end', value)} />
                                      {group.hours.length > 1 && (
                                        <button onClick={() => removeHourFromGroup(group.id, hours.id)} className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors ml-1">
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                  <button onClick={() => addHourToGroup(group.id)} className="text-sm font-medium text-emerald-400 hover:text-emerald-300 transition-colors flex items-center gap-1 mt-2">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg> Προσθήκη Ώρας
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      ))}

                      <button onClick={addDayGroup} className="w-full py-4 rounded-xl border-2 border-dashed border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 bg-[#1F2937] transition-all font-medium flex items-center justify-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg> Προσθήκη Ομάδας Ημερών
                      </button>
                    </div>
                  </div>

                  {/* Exception Days */}
                  <div className="rounded-2xl border bg-[#111827] border-gray-700 shadow-lg overflow-hidden mb-6">
                    <div className="px-6 py-5 border-b border-gray-700 bg-[#111827]">
                      <h3 className="text-lg font-semibold text-white">Ημέρες Κλεισίματος (Αργίες)</h3>
                      <p className="text-sm text-gray-400 mt-1">Ορίστε ημέρες που η κλινική θα είναι εντελώς κλειστή.</p>
                    </div>
                    <div className="p-6 space-y-4">
                      {exceptionDays.map((day) => (
                        <div key={day.id} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto_auto] items-center gap-4 p-4 rounded-xl border border-gray-700 bg-[#1F2937]">
                          <DatePicker
                            selected={day.date ? new Date(day.date + 'T00:00:00') : null}
                            onChange={(date) => {
                              if (date) {
                                const yyyy = date.getFullYear();
                                const mm = String(date.getMonth() + 1).padStart(2, '0');
                                const dd = String(date.getDate()).padStart(2, '0');
                                updateExceptionDay(day.id, 'date', `${yyyy}-${mm}-${dd}`);
                              } else {
                                updateExceptionDay(day.id, 'date', '');
                              }
                            }}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="Επιλέξτε ημερομηνία"
                            className="w-full px-4 py-2.5 bg-[#111827] border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:outline-none text-sm"
                            wrapperClassName="w-full"
                          />
                          <input
                            type="text"
                            placeholder="Λόγος κλεισίματος (π.χ. Χριστούγεννα)"
                            value={day.reason}
                            onChange={(e) => updateExceptionDay(day.id, 'reason', e.target.value)}
                            className="w-full px-4 py-2.5 bg-[#111827] border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:outline-none text-sm"
                          />
                          <label className="flex items-center gap-2 cursor-pointer text-gray-300 hover:text-white text-sm font-medium">
                            <input type="checkbox" checked={day.isYearly} onChange={(e) => updateExceptionDay(day.id, 'isYearly', e.target.checked)} className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-emerald-500 focus:ring-emerald-500" />
                            Κάθε χρόνο
                          </label>
                          {exceptionDays.length > 1 && (
                            <button onClick={() => removeExceptionDay(day.id)} className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                          )}
                        </div>
                      ))}
                      <button onClick={addExceptionDay} className="w-full py-3 rounded-xl border-2 border-dashed border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 bg-[#1F2937] transition-all font-medium flex items-center justify-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg> Προσθήκη Ημέρας
                      </button>
                    </div>
                  </div>

                  {/* Special Days */}
                  <div className="rounded-2xl border bg-[#111827] border-gray-700 shadow-lg overflow-hidden">
                    <div className="px-6 py-5 border-b border-gray-700 bg-[#111827]">
                      <h3 className="text-lg font-semibold text-white">Ειδικές Ημέρες</h3>
                      <p className="text-sm text-gray-400 mt-1">Ορίστε ημέρες με διαφορετικές ώρες λειτουργίας από το κανονικό σας πρόγραμμα.</p>
                    </div>
                    <div className="p-6 space-y-4">
                      {specialDays.map((day) => (
                        <div key={day.id} className="flex flex-col xl:flex-row items-start xl:items-center gap-4 p-4 rounded-xl border border-gray-700 bg-[#1F2937]">
                          <div className="flex-1 w-full grid grid-cols-1 md:grid-cols-2 gap-4">
                            <DatePicker
                              selected={day.date ? new Date(day.date + 'T00:00:00') : null}
                              onChange={(date) => {
                                if (date) {
                                  const yyyy = date.getFullYear();
                                  const mm = String(date.getMonth() + 1).padStart(2, '0');
                                  const dd = String(date.getDate()).padStart(2, '0');
                                  updateSpecialDay(day.id, 'date', `${yyyy}-${mm}-${dd}`);
                                } else {
                                  updateSpecialDay(day.id, 'date', '');
                                }
                              }}
                              dateFormat="dd/MM/yyyy"
                              placeholderText="Επιλέξτε ημερομηνία"
                              className="w-full px-4 py-2.5 bg-[#111827] border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:outline-none text-sm"
                              wrapperClassName="w-full"
                            />
                            <input
                              type="text"
                              placeholder="Περιγραφή (π.χ. Ημιαργία)"
                              value={day.description}
                              onChange={(e) => updateSpecialDay(day.id, 'description', e.target.value)}
                              className="w-full px-4 py-2.5 bg-[#111827] border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:outline-none text-sm"
                            />
                          </div>
                          
                          <div className="flex items-center gap-4 w-full xl:w-auto bg-[#111827] p-2 rounded-lg border border-gray-700">
                            <GreekTimeInput value={day.start} onChange={(value) => updateSpecialDay(day.id, 'start', value)} />
                            <span className="text-gray-500 text-sm">έως</span>
                            <GreekTimeInput value={day.end} onChange={(value) => updateSpecialDay(day.id, 'end', value)} />
                          </div>

                          <div className="flex items-center gap-4 w-full xl:w-auto justify-between xl:justify-end">
                            <label className="flex items-center gap-2 cursor-pointer text-gray-300 hover:text-white text-sm font-medium">
                              <input type="checkbox" checked={day.isYearly} onChange={(e) => updateSpecialDay(day.id, 'isYearly', e.target.checked)} className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-emerald-500 focus:ring-emerald-500" />
                              Κάθε χρόνο
                            </label>
                            <button onClick={() => removeSpecialDay(day.id)} className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                          </div>
                        </div>
                      ))}
                      <button onClick={addSpecialDay} className="w-full py-3 rounded-xl border-2 border-dashed border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 bg-[#1F2937] transition-all font-medium flex items-center justify-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg> Προσθήκη Ειδικής Ημέρας
                      </button>
                    </div>
                  </div>
                </div>

                {/* ═══ GROUP B: Ρυθμίσεις Κρατήσεων ═══ */}
                <div ref={sectionBookingsRef} className="scroll-mt-24">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="p-2.5 rounded-xl bg-blue-500/10 border border-blue-500/20">
                      <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Ρυθμίσεις Κρατήσεων</h2>
                      <p className="text-sm text-gray-400">Διάρκεια ραντεβού και χρονικό πλαίσιο κρατήσεων</p>
                    </div>
                  </div>

                  <div className="rounded-2xl border bg-[#111827] border-gray-700 shadow-lg overflow-hidden">
                    <div className="px-6 py-5 border-b border-gray-700 bg-[#111827]">
                      <h3 className="text-lg font-semibold text-white">Βασικές Παράμετροι</h3>
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-2">Διάρκεια Ραντεβού (λεπτά)</label>
                          <select value={appointmentDuration} onChange={(e) => setAppointmentDuration(Number(e.target.value))} className="w-full px-4 py-3 bg-[#1F2937] border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:outline-none">
                            <option value={15}>15 λεπτά</option>
                            <option value={30}>30 λεπτά</option>
                            <option value={45}>45 λεπτά</option>
                            <option value={60}>1 ώρα</option>
                            <option value={90}>1.5 ώρα</option>
                            <option value={120}>2 ώρες</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-2">Μέγιστοι Μήνες Προκαταβολής</label>
                          <select value={maxBookingMonths} onChange={(e) => setMaxBookingMonths(Number(e.target.value))} className="w-full px-4 py-3 bg-[#1F2937] border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:outline-none">
                            <option value={1}>1 μήνας</option>
                            <option value={2}>2 μήνες</option>
                            <option value={3}>3 μήνες</option>
                            <option value={6}>6 μήνες</option>
                            <option value={12}>1 έτος</option>
                          </select>
                        </div>
                      </div>

                      <div className="mt-8 pt-8 border-t border-gray-700">
                        <label className="block text-sm font-medium text-gray-400 mb-4">Επιβεβαίωση Ραντεβού</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <button type="button" onClick={() => setRequiresConfirmation(false)} className={`flex items-start p-5 rounded-xl border-2 transition-all text-left ${!requiresConfirmation ? 'border-emerald-500 bg-emerald-500/10 shadow-lg' : 'border-gray-700 bg-[#1F2937] hover:border-gray-500'}`}>
                            <div className={`mt-0.5 mr-4 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${!requiresConfirmation ? 'border-emerald-500 bg-emerald-500' : 'border-gray-500'}`}>
                              {!requiresConfirmation && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                            </div>
                            <div>
                              <p className="text-base font-bold text-white">Αυτόματη επιβεβαίωση</p>
                              <p className="text-sm text-gray-400 mt-1">Τα ραντεβού επιβεβαιώνονται αυτόματα κατά την κράτηση. Ιδανικό για γρήγορη εξυπηρέτηση.</p>
                            </div>
                          </button>
                          
                          <button type="button" onClick={() => setRequiresConfirmation(true)} className={`flex items-start p-5 rounded-xl border-2 transition-all text-left ${requiresConfirmation ? 'border-emerald-500 bg-emerald-500/10 shadow-lg' : 'border-gray-700 bg-[#1F2937] hover:border-gray-500'}`}>
                            <div className={`mt-0.5 mr-4 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${requiresConfirmation ? 'border-emerald-500 bg-emerald-500' : 'border-gray-500'}`}>
                              {requiresConfirmation && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                            </div>
                            <div>
                              <p className="text-base font-bold text-white">Χειροκίνητη έγκριση</p>
                              <p className="text-sm text-gray-400 mt-1">Τα ραντεβού απαιτούν έγκριση από την κλινική πριν επιβεβαιωθούν στον πελάτη.</p>
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ═══ GROUP C: Online Κράτηση ═══ */}
                <div ref={sectionOnlineBookingRef} className="scroll-mt-24">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="p-2.5 rounded-xl bg-violet-500/10 border border-violet-500/20">
                      <svg className="w-6 h-6 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Online Κράτηση</h2>
                      <p className="text-sm text-gray-400">Δημόσιο URL κράτησης για τους πελάτες σας</p>
                    </div>
                  </div>

                  <div className="rounded-2xl border bg-[#111827] border-gray-700 shadow-lg overflow-hidden">
                    <div className="p-6 md:p-8 space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-3">Προσαρμοσμένο URL (Slug)</label>
                        <div className="flex flex-col sm:flex-row gap-3">
                          <div className="relative flex-1">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-mono text-sm pointer-events-none">/book/</span>
                            <input
                              type="text"
                              value={clinicSlug}
                              onChange={(e) => setClinicSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                              placeholder="my-clinic"
                              className="w-full rounded-xl pl-16 pr-4 py-3 bg-[#1F2937] border border-gray-700 text-white font-medium focus:outline-none focus:ring-2 focus:ring-violet-500"
                            />
                          </div>
                          <button onClick={saveSlug} disabled={slugSaving || !clinicSlug.trim()} className="px-6 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 hover:scale-[1.02] transition-all shadow-md disabled:opacity-50">
                            {slugSaving ? 'Αποθήκευση...' : 'Αποθήκευση'}
                          </button>
                          <button onClick={generateSlugFromName} disabled={slugSaving} className="px-6 py-3 rounded-xl font-bold text-violet-400 bg-violet-500/10 border border-violet-500/30 hover:bg-violet-500/20 transition-colors disabled:opacity-50">
                            Αυτόματο
                          </button>
                        </div>
                      </div>

                      {clinicSlug && (
                        <div className="pt-6 border-t border-gray-700 flex flex-col md:flex-row gap-8">
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-400 mb-3">Το Link σας είναι έτοιμο</label>
                            <div className="flex gap-3">
                              <input
                                type="text"
                                readOnly
                                value={`${window.location.origin}/book/${clinicSlug}`}
                                className="flex-1 rounded-xl px-4 py-3 bg-[#1F2937] border border-gray-700 text-gray-300 font-mono text-sm outline-none"
                              />
                              <button onClick={copyBookingUrl} className="px-6 py-3 rounded-xl font-bold text-white bg-gray-700 hover:bg-gray-600 transition-colors shadow-md">
                                {slugCopied ? 'Αντιγράφηκε! ✓' : 'Αντιγραφή'}
                              </button>
                            </div>
                            <p className="text-sm text-gray-500 mt-4">Μοιραστείτε αυτό το link στα social media, στο website σας ή στείλτε το απευθείας στους πελάτες σας.</p>
                          </div>
                          <div className="flex flex-col items-center gap-3">
                            <div className="p-3 bg-white rounded-2xl shadow-lg">
                              <QRCodeSVG id="booking-qr-code" value={`${window.location.origin}/book/${clinicSlug}`} size={160} level="H" imageSettings={{ src: '/favicon.png', height: 40, width: 40, excavate: true }} />
                            </div>
                            <button onClick={downloadQR} className="text-sm font-medium text-violet-400 hover:text-violet-300 transition-colors flex items-center gap-1.5">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg> Κατέβασμα QR
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ═══ GROUP C2: Δημόσια Σελίδα ═══ */}
                <div ref={sectionPublicPageRef} className="scroll-mt-24">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2.5 rounded-xl bg-violet-500/10 border border-violet-500/20">
                      <svg className="w-6 h-6 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" /></svg>
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white flex items-center gap-3">
                        Δημόσια Σελίδα 
                        <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-violet-500 text-white shadow-lg">PRO</span>
                      </h2>
                      <p className="text-sm text-gray-400">Προσαρμόστε την εμφάνιση της σελίδας κρατήσεων</p>
                    </div>
                  </div>

                  {!canCustomize ? (
                    <div className="rounded-2xl border bg-[#111827] border-gray-700 shadow-lg overflow-hidden p-12 text-center">
                      <div className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-6 bg-violet-500/10 border border-violet-500/20">
                        <svg className="w-10 h-10 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-3">Premium Δυνατότητα</h3>
                      <p className="text-gray-400 max-w-md mx-auto text-lg leading-relaxed">
                        Αναβαθμίστε το πλάνο σας για να προσαρμόσετε τη δημόσια σελίδα κρατήσεών σας με το λογότυπο, τα χρώματα και το δικό σας κείμενο.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Logo Upload Card */}
                      <div className="rounded-2xl border bg-[#111827] border-gray-700 shadow-lg p-6">
                        <h3 className="text-lg font-semibold text-white mb-6">Λογότυπο Κλινικής</h3>
                        <div className="flex items-center gap-6">
                          <div className="flex-shrink-0">
                            {logoPreview ? (
                              <img src={logoPreview} alt="Λογότυπο κλινικής" className="w-24 h-24 rounded-full object-cover border-4 border-[#1F2937] shadow-xl" />
                            ) : (
                              <div className="w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold text-white shadow-inner" style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)` }}>
                                {clinic?.name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-3">
                            <label className={`inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-bold cursor-pointer transition-all ${logoUploading ? 'opacity-50 pointer-events-none' : 'hover:scale-[1.02] shadow-md'}`} style={{ backgroundColor: 'rgba(139, 92, 246, 0.15)', border: '1px solid rgba(139, 92, 246, 0.3)', color: '#C4B5FD' }}>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                              {logoUploading ? 'Ανέβασμα...' : 'Επιλογή Εικόνας'}
                              <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={handleLogoUpload} className="hidden" disabled={logoUploading} />
                            </label>
                            {logoPreview && (
                              <button onClick={handleLogoRemove} disabled={logoUploading} className="text-sm font-medium text-red-400 hover:text-red-300 transition-colors">
                                Αφαίρεση Λογοτύπου
                              </button>
                            )}
                            <p className="text-xs text-gray-500">Προτεινόμενο μέγεθος 256x256, max 500KB (PNG, JPG, WebP)</p>
                          </div>
                        </div>
                      </div>

                      {/* Brand Colors & Theme */}
                      <div className="rounded-2xl border bg-[#111827] border-gray-700 shadow-lg p-6">
                        <h3 className="text-lg font-semibold text-white mb-6">Στυλ & Χρώματα</h3>
                        
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-3">Κύριο Χρώμα</label>
                            <div className="flex gap-3 items-center">
                              <div className="relative w-12 h-12 rounded-xl overflow-hidden border-2 border-gray-600 shadow-inner flex-shrink-0">
                                <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="absolute -top-4 -left-4 w-24 h-24 cursor-pointer" />
                              </div>
                              <input type="text" value={primaryColor} onChange={(e) => { if (e.target.value.length <= 7) setPrimaryColor(e.target.value); }} placeholder="#10B981" className="w-full rounded-xl px-4 py-3 bg-[#1F2937] border border-gray-700 text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-3">Δευτερεύον Χρώμα</label>
                            <div className="flex gap-3 items-center">
                              <div className="relative w-12 h-12 rounded-xl overflow-hidden border-2 border-gray-600 shadow-inner flex-shrink-0">
                                <input type="color" value={accentColor} onChange={(e) => setAccentColor(e.target.value)} className="absolute -top-4 -left-4 w-24 h-24 cursor-pointer" />
                              </div>
                              <input type="text" value={accentColor} onChange={(e) => { if (e.target.value.length <= 7) setAccentColor(e.target.value); }} placeholder="#06B6D4" className="w-full rounded-xl px-4 py-3 bg-[#1F2937] border border-gray-700 text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
                            </div>
                          </div>
                        </div>

                        <div className="mb-8">
                          <label className="block text-sm font-medium text-gray-400 mb-4">Επιλογή Θέματος</label>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {Object.values(THEME_PRESETS).map((preset) => {
                              const isSelected = themeId === preset.id;
                              return (
                                <button
                                  key={preset.id}
                                  onClick={() => setThemeId(preset.id)}
                                  className="rounded-xl p-4 transition-all"
                                  style={{
                                    borderColor: isSelected ? primaryColor : '#4A5568',
                                    borderWidth: '2px',
                                    backgroundColor: isSelected ? 'rgba(139, 92, 246, 0.05)' : 'transparent',
                                    boxShadow: isSelected ? `0 4px 15px ${primaryColor}20` : 'none',
                                  }}
                                >
                                  <div className="rounded-lg overflow-hidden mb-3 border shadow-sm" style={{ borderColor: preset.cardBorder, height: '80px' }}>
                                    <div className="h-full p-2.5 flex flex-col justify-between" style={{ backgroundColor: preset.bg }}>
                                      <div className="rounded px-2 py-1.5 border" style={{ background: preset.cardBgStyle, borderColor: preset.cardBorder }}>
                                        <div className="h-2 rounded-full w-3/4 mb-1.5" style={{ backgroundColor: preset.textPrimary, opacity: 0.8 }} />
                                        <div className="h-1.5 rounded-full w-1/2" style={{ backgroundColor: preset.textSecondary, opacity: 0.5 }} />
                                      </div>
                                      <div className="h-5 rounded-md w-3/4 mx-auto" style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)` }} />
                                    </div>
                                  </div>
                                  <p className="text-sm font-bold text-center" style={{ color: isSelected ? primaryColor : '#D1D5DB' }}>{preset.nameGr}</p>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div className="pt-6 border-t border-gray-700 flex justify-between items-center">
                          <p className="text-sm text-gray-400">Δείτε πώς φαίνονται τα χρώματα στο κουμπί κράτησης:</p>
                          <div className="px-8 py-3 rounded-full text-sm font-bold text-white shadow-lg" style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)` }}>
                            Επόμενο Βήμα
                          </div>
                        </div>
                      </div>

                      {/* Intro Text Card */}
                      <div className="rounded-2xl border bg-[#111827] border-gray-700 shadow-lg p-6">
                        <h3 className="text-lg font-semibold text-white mb-2">Κείμενο Υποδοχής</h3>
                        <p className="text-sm text-gray-400 mb-4">Ένα μικρό μήνυμα που καλωσορίζει τους πελάτες στη σελίδα κρατήσεων.</p>
                        
                        <textarea
                          value={publicIntroText}
                          onChange={(e) => {
                            const text = e.target.value.replace(/<[^>]*>/g, '');
                            if (text.length <= 200) setPublicIntroText(text);
                          }}
                          placeholder="π.χ. Καλώς ήρθατε στην κλινική μας! Επιλέξτε την υπηρεσία που επιθυμείτε."
                          rows={3}
                          className="w-full rounded-xl px-4 py-3 text-white text-base focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none bg-[#1F2937] border border-gray-700"
                        />
                        <p className="text-xs text-gray-500 mt-2 text-right font-medium">{publicIntroText.length}/200</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* ═══ GROUP D: Ρυθμίσεις Υπενθύμισης Ραντεβού ═══ */}
                <div ref={sectionRemindersRef} className="scroll-mt-24">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20">
                      <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Πρότυπα Email</h2>
                      <p className="text-sm text-gray-400">Διαμορφώστε τα αυτοματοποιημένα emails προς τους πελάτες</p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {/* Reminder Email */}
                    <div className="rounded-2xl border bg-[#111827] border-gray-700 shadow-lg p-6">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
                        <div>
                          <h3 className="text-lg font-semibold text-white">Email Υπενθύμισης</h3>
                          <p className="text-sm text-gray-400 mt-1">Στέλνεται αυτόματα 2-3 ώρες πριν το ραντεβού.</p>
                        </div>
                        <button onClick={() => { setAppointmentEmailTemplate(generateDefaultTemplate()); setIsDirty(true); setTemplateError(''); }} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors border border-gray-600 flex items-center gap-2 whitespace-nowrap">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg> Επαναφορά
                        </button>
                      </div>

                      <div className="mb-4 flex flex-wrap gap-2 text-xs">
                        <span className="text-gray-500 font-medium pt-1">Απαραίτητα Tags:</span>
                        <code className="bg-[#1F2937] border border-gray-700 px-2 py-1 rounded text-amber-400 font-mono">{'{{pet_name}}'}</code>
                        <code className="bg-[#1F2937] border border-gray-700 px-2 py-1 rounded text-amber-400 font-mono">{'{{appointment_date}}'}</code>
                        <code className="bg-[#1F2937] border border-gray-700 px-2 py-1 rounded text-amber-400 font-mono">{'{{owner_name}}'}</code>
                      </div>

                      <textarea
                        value={appointmentEmailTemplate}
                        onChange={(e) => { setAppointmentEmailTemplate(e.target.value); setIsDirty(true); setTemplateError(validateTemplate(e.target.value) || ''); }}
                        rows={6}
                        className="w-full px-4 py-4 rounded-xl text-white text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-amber-500/50 bg-[#1F2937] border border-gray-700 resize-y"
                        placeholder="Εισάγετε το κείμενο υπενθύμισης..."
                      />
                      {templateError && <p className="mt-2 text-red-400 text-sm font-medium flex items-center gap-1"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> {templateError}</p>}
                    </div>

                    {/* Confirmation Email */}
                    <div className="rounded-2xl border bg-[#111827] border-gray-700 shadow-lg p-6">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
                        <div>
                          <h3 className="text-lg font-semibold text-white">Email Επιβεβαίωσης</h3>
                          <p className="text-sm text-gray-400 mt-1">Στέλνεται όταν δημιουργείται (ή εγκρίνεται) ένα ραντεβού.</p>
                        </div>
                        <button onClick={() => { setAppointmentConfirmationTemplate(generateDefaultConfirmationTemplate()); setIsDirty(true); setConfirmationTemplateError(''); }} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors border border-gray-600 flex items-center gap-2 whitespace-nowrap">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg> Επαναφορά
                        </button>
                      </div>

                      <div className="mb-4 flex flex-wrap gap-2 text-xs">
                        <span className="text-gray-500 font-medium pt-1">Απαραίτητα Tags:</span>
                        <code className="bg-[#1F2937] border border-gray-700 px-2 py-1 rounded text-amber-400 font-mono">{'{{pet_name}}'}</code>
                        <code className="bg-[#1F2937] border border-gray-700 px-2 py-1 rounded text-amber-400 font-mono">{'{{appointment_date}}'}</code>
                        <code className="bg-[#1F2937] border border-gray-700 px-2 py-1 rounded text-amber-400 font-mono">{'{{owner_name}}'}</code>
                      </div>

                      <textarea
                        value={appointmentConfirmationTemplate}
                        onChange={(e) => { setAppointmentConfirmationTemplate(e.target.value); setIsDirty(true); setConfirmationTemplateError(validateTemplate(e.target.value) || ''); }}
                        rows={6}
                        className="w-full px-4 py-4 rounded-xl text-white text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-amber-500/50 bg-[#1F2937] border border-gray-700 resize-y"
                        placeholder="Εισάγετε το κείμενο επιβεβαίωσης..."
                      />
                      {confirmationTemplateError && <p className="mt-2 text-red-400 text-sm font-medium flex items-center gap-1"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> {confirmationTemplateError}</p>}
                    </div>
                  </div>
                </div>

                {/* ═══ GROUP E: Έλεγχος Πρόσβασης ═══ */}
                <div ref={sectionAccessRef} className="scroll-mt-24">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="p-2.5 rounded-xl bg-red-500/10 border border-red-500/20">
                      <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Έλεγχος Πρόσβασης</h2>
                      <p className="text-sm text-gray-400">Διαχείριση αποκλεισμένων επαφών</p>
                    </div>
                  </div>

                  <div className="rounded-2xl border bg-[#111827] border-gray-700 shadow-lg overflow-hidden">
                    <div className="px-6 py-5 border-b border-gray-700 bg-[#111827]">
                      <h3 className="text-lg font-semibold text-white">Αποκλεισμένες Επαφές (Blacklist)</h3>
                      <p className="text-sm text-gray-400 mt-1">Άτομα στη λίστα δεν μπορούν να κλείσουν ραντεβού από τη δημόσια σελίδα.</p>
                    </div>
                    <div className="p-6">
                      
                      {/* Add form */}
                      <div className="p-5 rounded-xl border border-gray-700 bg-[#1F2937] mb-8 shadow-sm">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <input type="email" placeholder="Email" value={bannedForm.email} onChange={(e) => setBannedForm({ ...bannedForm, email: e.target.value })} className="w-full px-4 py-2.5 bg-[#111827] border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-red-500/50 focus:outline-none text-sm" />
                          <input type="tel" placeholder="Τηλέφωνο" value={bannedForm.phone} onChange={(e) => setBannedForm({ ...bannedForm, phone: e.target.value })} className="w-full px-4 py-2.5 bg-[#111827] border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-red-500/50 focus:outline-none text-sm" />
                          <input type="text" placeholder="Αιτιολογία (προαιρετικό)" value={bannedForm.reason} onChange={(e) => setBannedForm({ ...bannedForm, reason: e.target.value })} className="w-full px-4 py-2.5 bg-[#111827] border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-red-500/50 focus:outline-none text-sm" />
                        </div>
                        <button onClick={addBannedContact} disabled={bannedSaving || (!bannedForm.email && !bannedForm.phone)} className="px-6 py-2.5 bg-gray-700 hover:bg-red-600 text-white rounded-lg transition-colors font-bold disabled:opacity-50 border border-gray-600 hover:border-red-500 shadow-sm">
                          {bannedSaving ? 'Αποθήκευση...' : '+ Αποκλεισμός Επαφής'}
                        </button>
                      </div>

                      {/* List */}
                      {bannedContacts.length === 0 ? (
                        <div className="text-center py-10 border-2 border-dashed border-gray-700 rounded-xl bg-[#111827]">
                          <svg className="mx-auto h-12 w-12 text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                          <p className="text-gray-400 font-medium">Δεν υπάρχουν αποκλεισμένες επαφές</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {bannedContacts.map((contact) => (
                            <div key={contact.id} className="flex items-center justify-between p-4 rounded-xl border border-gray-700 bg-[#1F2937] hover:border-gray-600 transition-colors">
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-4 text-sm mb-1">
                                  {contact.email && <span className="text-white font-medium bg-[#111827] px-3 py-1 rounded-md border border-gray-700">{contact.email}</span>}
                                  {contact.phone && <span className="text-white font-medium bg-[#111827] px-3 py-1 rounded-md border border-gray-700">{contact.phone}</span>}
                                </div>
                                {contact.reason && <p className="text-sm text-gray-400 mt-2 ml-1 flex items-center gap-2"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> {contact.reason}</p>}
                              </div>
                              <button onClick={() => removeBannedContact(contact.id)} className="ml-4 p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors border border-transparent hover:border-red-500/20" title="Αφαίρεση">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </>
          )}
        </div>

        {/* Sticky Save Footer */}
        {clinicId && (
          <div className="absolute bottom-0 left-0 right-0 z-40 px-6 py-5 border-t border-gray-700 bg-[#111827]/95 backdrop-blur-md shadow-[0_-10px_30px_rgba(0,0,0,0.5)]">
            <div className="flex items-center justify-between max-w-full">
              <div>
                {isDirty && (
                  <span className="text-amber-400 text-sm font-bold flex items-center gap-2 animate-pulse bg-amber-500/10 px-3 py-1.5 rounded-lg border border-amber-500/20">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                    Έχετε μη αποθηκευμένες αλλαγές
                  </span>
                )}
              </div>
              <button
                onClick={saveSettings}
                disabled={saving || !isDirty}
                className={`px-8 py-3 font-bold rounded-xl transition-all duration-300 shadow-lg flex items-center gap-2 ${
                  saving || !isDirty
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:scale-[1.02] text-white hover:shadow-emerald-500/20'
                }`}
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Αποθήκευση...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                    Αποθήκευση Ρυθμίσεων
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {isSidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={() => setIsSidebarOpen(false)} />
      )}

      {/* Unsaved Changes Modal */}
      {showLeaveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl bg-[#111827] border border-gray-700">
            <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white text-center mb-2">Μη αποθηκευμένες αλλαγές</h3>
            <p className="text-gray-400 text-sm text-center mb-6">Έχετε αλλαγές που δεν έχουν αποθηκευτεί. Αν φύγετε τώρα, οι αλλαγές θα χαθούν οριστικά.</p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setShowLeaveModal(false);
                  pendingPopBack.current = false;
                  if (blocker.state === 'blocked') blocker.reset();
                }}
                className="w-full py-3 text-sm font-bold text-white bg-[#374151] hover:bg-gray-600 rounded-xl transition-colors"
              >
                Παραμείνετε & Αποθήκευση
              </button>
              <button
                onClick={() => {
                  setShowLeaveModal(false);
                  if (pendingPopBack.current) {
                    pendingPopBack.current = false;
                    bypassBlockerRef.current = true;
                    navigate(-1);
                  } else if (blocker.state === 'blocked') {
                    blocker.proceed();
                  }
                }}
                className="w-full py-3 text-sm font-bold text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl transition-colors border border-transparent hover:border-red-500/20"
              >
                Ναι, θέλω να φύγω
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <div className="fixed top-4 right-4 left-4 sm:left-auto z-[60]">
          <div
            className="flex items-center px-5 py-4 rounded-xl border max-w-sm ml-auto transition-all duration-300 shadow-2xl"
            style={{
              background: 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(31,41,55,0.95) 100%)',
              backdropFilter: 'blur(10px)',
              borderColor: toast.type === 'success' ? '#10B981' : toast.type === 'error' ? '#EF4444' : '#3B82F6',
            }}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${toast.type === 'success' ? 'bg-emerald-500/20' : toast.type === 'error' ? 'bg-red-500/20' : 'bg-blue-500/20'}`}>
              {toast.type === 'success' && <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>}
              {toast.type === 'error' && <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>}
              {toast.type === 'info' && <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            </div>
            <div className="text-white font-medium flex-1 text-sm">{toast.message}</div>
            <button onClick={() => setToast({ show: false, message: '', type: 'success' })} className="ml-4 text-gray-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;